import { Box, Flex, Icon, Text } from '@chakra-ui/react';
import { LuInfo } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../../utils/intlFormatters';
import { LINE_FORMATS, TONES, moneyText, signedMoney } from '../repairsUI';

// "−COP 45,000.00" for money going out, "+COP 45,000.00" for money coming in.
export const directedMoney = (side, currency) => (side?.amount == null
  ? '—'
  : signedMoney(side.direction === 'out' ? -Math.abs(side.amount) : Math.abs(side.amount), currency));

export const AMOUNT_COLOR = { in: 'redesign.amount.income', out: 'redesign.amount.expense' };

// A small rounded label: "New", "Removed", "You edited this".
export function Tag({ tone = 'muted', children }) {
  const tones = {
    blue: { bg: 'phone.newPillBg', color: 'phone.newPillFg' },
    orange: { bg: 'phone.alertBg', color: 'phone.alertText' },
    green: TONES.green,
    red: TONES.red,
    muted: { bg: 'redesign.bg.card', color: 'redesign.text.muted' },
  };
  const style = tones[tone] || tones.muted;
  return (
    <Box
      as="span"
      display="inline-flex"
      alignItems="center"
      px={2}
      py="1px"
      borderRadius="full"
      fontSize="2xs"
      fontWeight={600}
      whiteSpace="nowrap"
      flexShrink={0}
      {...style}
    >
      {children}
    </Box>
  );
}

/**
 * How a review names a movement: its note as the Movements list shows it,
 * else its category, else "Movement"; and under it its date and category.
 * `detail` is the movement as `GET /movements/{id}` answers (null while it
 * loads or when it is gone); `facts` what the check carries of it.
 */
export function movementLabel(detail, facts, names, t) {
  const note = detail?.note?.trim();
  const categoryId = detail?.category_id ?? facts?.category_id;
  const category = categoryId != null ? names.categories[categoryId] : null;
  const date = detail?.date || facts?.date;
  // A movement named by its category does not repeat it underneath.
  return {
    title: note || category || t('checkup.changes.movement'),
    meta: [date && formatDate(date), note && (category || t('checkup.changes.noCategory')), !note && !category && t('checkup.changes.noCategory')]
      .filter(Boolean).join(' · '),
  };
}

// One figure of the equation: a caption on the first line only.
function Figure({ value, currency, strong }) {
  return (
    <Text
      fontSize="sm"
      fontWeight={strong ? 600 : 500}
      color="redesign.heading"
      whiteSpace="nowrap"
      sx={{ fontVariantNumeric: 'tabular-nums' }}
    >
      {value == null ? '—' : moneyText(value, currency)}
    </Text>
  );
}

function Op({ children }) {
  return <Text fontSize="sm" color="redesign.text.muted" textAlign="center">{children}</Text>;
}

// Whether a reading's rows miss its closing: the backend's verdict, which
// lets a cent and the bank's round-up of what is owed add up.
const missesClosing = (side) => side.addsUp === false;

function Verdict({ side, currency }) {
  const { t } = useTranslation('repairs');
  if (side.residual == null) return null;
  const off = missesClosing(side);
  return (
    <Box
      as="span"
      justifySelf="start"
      px={2}
      py="2px"
      borderRadius="full"
      fontSize="2xs"
      fontWeight={600}
      whiteSpace="nowrap"
      {...(off ? TONES.orange : TONES.green)}
    >
      {off
        ? t('checkup.sums.offBy', { amount: moneyText(Math.abs(side.residual), currency) })
        : t(side.rounded ? 'checkup.sums.rounded' : 'checkup.sums.addsUp')}
    </Box>
  );
}

/**
 * A statement's own arithmetic, the way the review opens with it: the
 * opening printed on it, plus its own movements, against the closing printed
 * on it; as read before and, after a re-read, as read again. One block per
 * account and currency. On the phone each reading is a short list.
 */
export function StatementSums({ sums, ctx, isPhone }) {
  const { t } = useTranslation('repairs');
  const printed = sums.filter((entry) => entry.printed);
  if (!printed.length) return null;
  const several = printed.length > 1;
  // A side with no balances says nothing, unless it is the only one.
  const sides = (entry) => [
    ...(entry.after && (entry.before.opening == null || entry.before.closing == null) ? [] : [['before', entry.before]]),
    ...(entry.after ? [['after', entry.after]] : []),
  ];
  return (
    <Flex direction="column" gap={3}>
      {printed.map((entry) => (
        <Box key={`${entry.accountId}-${entry.currency}`} bg="redesign.bg.card" borderRadius="10px" p={[3, 3, 4]}>
          {several && (
            <Text fontSize="xs" fontWeight={600} color="redesign.heading" mb={2}>
              {LINE_FORMATS.account(entry.accountId, { ...ctx, t })}
              {entry.currency ? ` · ${entry.currency}` : ''}
            </Text>
          )}
          {isPhone ? (
            <Flex direction="column" gap={3}>
              {sides(entry).map(([key, side]) => (
                <Box key={key}>
                  <Flex justify="space-between" align="center" mb={1}>
                    <Text fontSize="xs" fontWeight={600} color="redesign.text.muted">{t(`checkup.sums.${key}`)}</Text>
                    <Verdict side={side} currency={entry.currency} />
                  </Flex>
                  {[['opening', side.opening], ['rows', side.rows], ['closing', side.closing]].map(([label, value]) => (
                    <Flex key={label} justify="space-between" py="2px">
                      <Text fontSize="xs" color="redesign.text.muted">{t(`checkup.sums.${label}`)}</Text>
                      <Figure value={value} currency={entry.currency} />
                    </Flex>
                  ))}
                </Box>
              ))}
            </Flex>
          ) : (
            <Box
              display="grid"
              gridTemplateColumns="130px auto 20px auto 20px auto 24px auto minmax(0, 1fr)"
              columnGap={2}
              rowGap={2}
              alignItems="center"
            >
              <span />
              <Text fontSize="2xs" color="redesign.text.muted">{t('checkup.sums.opening')}</Text>
              <span />
              <Text fontSize="2xs" color="redesign.text.muted">{t('checkup.sums.rows')}</Text>
              <span />
              <Text fontSize="2xs" color="redesign.text.muted">{t('checkup.sums.total')}</Text>
              <span />
              <Text fontSize="2xs" color="redesign.text.muted">{t('checkup.sums.closing')}</Text>
              <span />
              {sides(entry).map(([key, side]) => {
                const total = side.opening != null && side.rows != null ? side.opening + side.rows : null;
                const off = missesClosing(side);
                return [
                  <Text key={`${key}-label`} fontSize="xs" color="redesign.text.muted">{t(`checkup.sums.${key}`)}</Text>,
                  <Figure key={`${key}-opening`} value={side.opening} currency={entry.currency} />,
                  <Op key={`${key}-plus`}>+</Op>,
                  <Figure key={`${key}-rows`} value={side.rows} currency={entry.currency} />,
                  <Op key={`${key}-equals`}>=</Op>,
                  <Figure key={`${key}-total`} value={total} currency={entry.currency} strong />,
                  <Op key={`${key}-vs`}>{off ? '≠' : side.rounded ? '≈' : '='}</Op>,
                  <Figure key={`${key}-closing`} value={side.closing} currency={entry.currency} strong />,
                  <Verdict key={`${key}-verdict`} side={side} currency={entry.currency} />,
                ];
              })}
            </Box>
          )}
        </Box>
      ))}
      <Flex align="flex-start" gap={2}>
        <Icon as={LuInfo} boxSize="14px" color="redesign.text.muted" mt="2px" flexShrink={0} />
        <Text fontSize="xs" color="redesign.text.muted">{t('checkup.sums.onlyThisFile')}</Text>
      </Flex>
    </Flex>
  );
}

// "→" between a before and an after.
export function Arrow() {
  return <Text as="span" fontSize="xs" color="redesign.text.muted" aria-hidden="true">→</Text>;
}

// A before → after pair on one line, the before struck through.
export function BeforeAfter({ before, after, beforeColor = 'redesign.text.muted', afterColor = 'redesign.heading' }) {
  return (
    <Flex align="center" gap={2} justify="flex-end" wrap="wrap" sx={{ fontVariantNumeric: 'tabular-nums' }}>
      {before != null && (
        <Text as="span" fontSize="xs" color={beforeColor} textDecoration="line-through">{before}</Text>
      )}
      {before != null && <Arrow />}
      <Text as="span" fontSize="sm" fontWeight={600} color={afterColor}>{after}</Text>
    </Flex>
  );
}
